// client/src/routes/app/equipment/$equipmentId.tsx — SCR-EQP-002 · MAINT-006 (04 §8.9; the file
// path is §7's own tree, „app/equipment/$equipmentId.tsx  # SCR-EQP-002").
//
// THIS IS THE DEEP-LINK TARGET, and 04 §8.9 is explicit about what landing means here: „deep
// links from Action Center land directly on the flow". So the route reads TWO things off the
// URL — the equipment id from the path, and `?action=revizie` from the search — and a link that
// carries both opens the complete-revision dialog on the equipment it names, cold: a fresh tab,
// an e-mail, a notification. 05 MAINT-006's criterion („completing a revision from the task
// deep-link works end-to-end") is that link followed to its end, and the suite walks it.
//
// The search param is spelled in Romanian because it is user-visible URL text in a Romanian-only
// product (04 §0.1), and it is a `z.enum` of one member rather than a boolean: a second flow on
// this screen — „programează", say — becomes a second member instead of a second parameter.
//
// ONE PAYLOAD FEEDS THE SCREEN. The header, the facts and the timeline all come from
// `GET /equipment/{id}`; the LADDER comes from the organization's notification rule, which is a
// different resource and is fetched as one (see `features/equipment/ladder.ts`). Nothing on the
// screen re-derives the scadență — 03 §11.7 makes it the server's, and no endpoint accepts one.
//
// EDITING IS INLINE rather than a fourth route, the way SCR-REP-002 does it: the editable half
// is ten fields and the form is the create form. Deleting is logical (`DELETE` sets `active`
// away and keeps the history), so its confirmation says that rather than „această acțiune este
// ireversibilă", which would not be true.
//
// EVERY WRITE HERE IS THE APARTMENT'S (BR-COOWN-09 rule 5, D-2466): a revision, an edit and the
// retirement all write the equipment, which is its flat's, so a co-owner who does not run that
// flat reads the whole screen and is told, where the three actions were, who does. The deep
// link's dialog opens only for a caller who may save it; a task that says „programează revizia"
// is routed to the administrator anyway (D-1988).

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { EquipmentDetail } from '@/client';
import { Icon } from '@/components/icons';
import { SummaryPanel, SummaryPill } from '@/components/motion/SummaryPanel';
import { DetailHeader } from '@/components/layout/DetailHeader';
import { RequirePermission } from '@/components/layout/route-guards';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { AdministrationNotice, usePropertyWrite } from '@/features/properties/administration';
import { CompleteRevisionDialog } from '@/features/equipment/CompleteRevisionDialog';
import { EquipmentFacts } from '@/features/equipment/EquipmentFacts';
import { EquipmentForm } from '@/features/equipment/EquipmentForm';
import { EquipmentTypeIcon } from '@/features/equipment/EquipmentTypeIcon';
import { useEquipmentErrorMessage } from '@/features/equipment/errors';
import { equipmentTypeLabel } from '@/features/equipment/labels';
import { useRevisionLadder } from '@/features/equipment/ladder';
import { useDeleteEquipment, useEquipment, useUpdateEquipment } from '@/features/equipment/queries';
import { RevisionDueChip } from '@/features/equipment/RevisionDueChip';
import { RevisionTimeline } from '@/features/equipment/RevisionTimeline';
import { toEquipmentInput, type EquipmentFormValues } from '@/features/equipment/schema';
import { todayInBucharest } from '@/features/payments/today';
import { formatDate } from '@/lib/format';
import { wireNumberOrNull } from '@/lib/wire';
import { toast } from '@/stores/toast-store';

/** The flows a link may open on arrival. One member today; a second one is a second member. */
const equipmentDetailSearchSchema = z.object({
  action: z.enum(['revizie']).optional(),
});

export const Route = createFileRoute('/app/equipment/$equipmentId')({
  validateSearch: equipmentDetailSearchSchema,
  component: EquipmentDetailScreen,
});

function EquipmentDetailScreen() {
  const { equipmentId } = Route.useParams();
  const { action } = Route.useSearch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const equipment = useEquipment(equipmentId);
  const { canWrite, blockedByAdministration, administratorName } = usePropertyWrite(
    equipment.data?.propertyId,
    'Operations.Write',
  );
  const ladder = useRevisionLadder();
  const update = useUpdateEquipment();
  const describe = useEquipmentErrorMessage();

  const [editing, setEditing] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  // The deep link's own state: `?action=revizie` opens the flow, and closing it clears the
  // param so a refresh does not reopen a dialog the landlord has just dismissed.
  const [revisionOpen, setRevisionOpen] = useState(action === 'revizie');

  if (equipment.isPending) return <LoadingState rows={4} />;
  if (equipment.isError) {
    return <ErrorState error={equipment.error} onRetry={() => void equipment.refetch()} />;
  }

  const detail = equipment.data;

  const submit = async (values: EquipmentFormValues) => {
    setFailure(null);
    try {
      await update.mutateAsync({ path: { id: detail.id }, body: toEquipmentInput(values) });
      setEditing(false);
      toast.success(t('equipment.form.saved'));
    } catch (error) {
      setFailure(describe(error));
    }
  };

  const closeRevision = () => {
    setRevisionOpen(false);
    if (action !== undefined) void navigate({ to: '.', search: {}, replace: true });
  };

  // „Vaillant ecoTEC" — the identity line under the title, when the register knows it.
  const identity = [detail.manufacturer, detail.model]
    .filter((part) => part != null && part !== '')
    .join(' ');

  return (
    <RequirePermission permission="Operations.Read">
      <DetailHeader
        backTo="/app/equipment"
        backLabel={t('equipment.detail.backToRegister')}
        glyph="wrench"
        title={equipmentTypeLabel(detail, t)}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-x-2">
            <EquipmentTypeIcon equipmentType={detail.equipmentType} className="h-4 w-4" />
            {[detail.propertyName, identity].filter((part) => part).join(' · ')}
          </span>
        }
        chips={
          <>
            <RevisionDueChip
              nextRevisionDate={detail.nextRevisionDate}
              daysUntilRevision={wireNumberOrNull(detail.daysUntilRevision)}
              ladder={ladder}
            />
            {!detail.active ? (
              <span className="chip chip-neutral">{t('equipment.list.retired')}</span>
            ) : null}
          </>
        }
        actions={
          editing ? undefined : (
            <>
              {canWrite ? (
                <>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => setRevisionOpen(true)}
                  >
                    <Icon name="check-circle" className="h-4 w-4" />
                    {t('equipment.actions.recordRevision')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => {
                      setFailure(null);
                      setEditing(true);
                    }}
                  >
                    <Icon name="cog" className="h-4 w-4" />
                    {t('equipment.actions.edit')}
                  </button>
                  <DeleteEquipmentAction equipmentId={detail.id} />
                </>
              ) : blockedByAdministration ? (
                <AdministrationNotice name={administratorName} />
              ) : null}
            </>
          )
        }
      />

      {editing ? (
        <EquipmentForm
          equipment={detail}
          submitting={update.isPending}
          failure={failure}
          onSubmit={(values) => void submit(values)}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <>
          <RevisionSummary equipment={detail} />
          <div className="grid gap-6 lg:grid-cols-2">
            <EquipmentFacts equipment={detail} />
            <RevisionTimeline equipment={detail} ladder={ladder} today={todayInBucharest()} />
          </div>
        </>
      )}

      {revisionOpen && canWrite ? (
        <CompleteRevisionDialog equipment={detail} onClose={closeRevision} />
      ) : null}
    </RequirePermission>
  );
}

/**
 * „Situația reviziei" (D-3806): the page's summary panel — the next scadență as the headline, the
 * last revision as a pill (the interval and the open tasks stay in the facts beside it, said
 * once), compact and with no drawing (D-3815). Every figure is the detail payload's; the verbs
 * stay in the header.
 */
function RevisionSummary({ equipment }: { equipment: EquipmentDetail }) {
  const { t } = useTranslation();

  return (
    <SummaryPanel
      title={t('equipment.detailSummary.title')}
      icon="calendar"
      compact
      className="mb-6"
    >
      <p className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-3xl font-bold tracking-tight text-base-content tabular-nums">
          {equipment.nextRevisionDate == null
            ? t('equipment.due.noSchedule')
            : formatDate(equipment.nextRevisionDate)}
        </span>
        {equipment.nextRevisionDate == null ? null : (
          <span className="text-sm text-gray-500">{t('equipment.detailSummary.nextCaption')}</span>
        )}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <SummaryPill caption={t('equipment.detailSummary.lastRevision')}>
          {equipment.lastRevisionDate == null ? '—' : formatDate(equipment.lastRevisionDate)}
        </SummaryPill>
      </div>
    </SummaryPanel>
  );
}

/**
 * The register's own retirement, with the sentence a logical delete deserves: the row leaves the
 * register and the revisions it carries stay behind, which is what `DeleteEquipment`'s summary
 * says on the server („Ștergere logică; istoricul de revizii rămâne").
 */
function DeleteEquipmentAction({ equipmentId }: { equipmentId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const remove = useDeleteEquipment();
  const describe = useEquipmentErrorMessage();

  const [open, setOpen] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const confirm = async () => {
    setFailure(null);
    try {
      await remove.mutateAsync({ path: { id: equipmentId } });
      toast.success(t('equipment.detail.deleted'));
      await navigate({ to: '/app/equipment' });
    } catch (error) {
      setFailure(describe(error));
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => {
          setFailure(null);
          setOpen(true);
        }}
      >
        <Icon name="x-circle" className="h-4 w-4" />
        {t('common.delete')}
      </button>

      {/* `warning`, not `danger`: the delete is logical and the body says what stays. */}
      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={() => void confirm()}
        title={t('equipment.detail.deleteTitle')}
        description={t('equipment.detail.deleteBody')}
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        tone="warning"
      >
        {failure !== null ? (
          <p role="alert" className="text-sm text-red-700">
            {failure}
          </p>
        ) : null}
      </ConfirmDialog>
    </>
  );
}
